// smartObjectPreview — TRUE-preview service for Smart Object recipes: generates
// a recipe at its DEFAULTS (the exact object a click inserts), renders it to an
// offscreen StaticCanvas, and hands back a transparent PNG thumbnail. Same
// discipline as inspector/text/stylePreviewRenderer.js (shared canvas, single
// serialized render, LRU of object URLs) so a tile can't drift from what
// addSmartObject paints. Two public renders share the one canvas + queue:
//   renderSmartObjectThumb   — defaults, LRU-cached (browser tiles; SM-B lane)
//   renderSmartObjectPreview — draft params, UNCACHED, caller-owned URL
//                              (the SmartToolModal's live preview pane)
//
// - ONE shared offscreen StaticCanvas, lazily created; renders SERIALIZE
//   through a promise chain (the canvas is mutable — dimensions + viewport —
//   so strictly one at a time).
// - The recipe generates ONLY stock fabric types (Group/Textbox/Rect/…). We
//   fit-and-center its world bounding box into a square canvas via the canvas
//   viewportTransform (getBoundingRect is viewport-independent in fabric v6),
//   so the object's own geometry is never mutated.
// - Output is a PNG object URL (async toBlob; ~a few KB per thumb). Callers put
//   it straight on an <img src>. Canvas stays TRANSPARENT — the tile supplies
//   its own light backdrop behind the <img>.
// - LRU cache (cap 200) keyed on recipe id + version + size. Evicted/cleared
//   entries revoke their object URLs.
// - Fonts a recipe's defaults reference are ensured (same collect+ensureFont
//   the sidebar's insert path uses) BEFORE generate, so text measures against
//   the real face. Soft-fail: a missing font never fails the thumb.
// - generate() is async and can throw (a recipe needing data/assets). On ANY
//   failure the promise resolves to null and the tile shows its fallback.

import { StaticCanvas } from 'fabric';
import { generateSmartObject, getRecipe } from '../../../posterTools';
import { ensureFont } from './fonts/fontLoader';
import { collectDraftFontFamilies } from './SmartObjectFieldUtils';

const CACHE_CAP = 200;

// ---------------------------------------------------------------- LRU cache
const cache = new Map(); // key -> object URL (Map order = LRU order)

function cacheGet(key) {
  if (!cache.has(key)) return null;
  const url = cache.get(key);
  cache.delete(key);
  cache.set(key, url); // bump to most-recent
  return url;
}

function cachePut(key, url) {
  if (cache.has(key)) {
    const old = cache.get(key);
    cache.delete(key);
    if (old !== url) { try { URL.revokeObjectURL(old); } catch { /* gone */ } }
  }
  cache.set(key, url);
  while (cache.size > CACHE_CAP) {
    const [oldestKey, oldestUrl] = cache.entries().next().value;
    cache.delete(oldestKey);
    try { URL.revokeObjectURL(oldestUrl); } catch { /* gone */ }
  }
}

function keyFor(recipeId, version, size) {
  return `so|${recipeId}|v${version ?? '?'}|${size}`;
}

// ------------------------------------------------------------- shared canvas
let sharedCanvas = null;

function getCanvas(size) {
  if (!sharedCanvas) {
    sharedCanvas = new StaticCanvas(undefined, {
      width: size,
      height: size,
      renderOnAddRemove: false,
      enableRetinaScaling: false,
    });
  } else {
    sharedCanvas.setDimensions({ width: size, height: size });
  }
  // Always start from an identity viewport so a prior errored render can't
  // leave a stale transform behind (we also reset in the finally below).
  sharedCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
  return sharedCanvas;
}

// One render at a time — the canvas is shared and mutable. The chain never
// rejects (same discipline as the engine's chained FX); callers get the real
// call's promise.
let queue = Promise.resolve();
function enqueue(run) {
  const p = queue.then(run, run);
  queue = p.then(() => undefined, () => undefined);
  return p;
}

function toBlob(canvasEl) {
  return new Promise((resolve, reject) => {
    canvasEl.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('smart-object thumb toBlob returned null'))),
      'image/png',
    );
  });
}

// ------------------------------------------------------------------- render
// `params` (optional) merges over the recipe defaults — the thumb path passes
// none (defaults = the exact object addSmartObject inserts); the modal's live
// preview passes the current draft params.
async function renderOnce(recipeId, size, params) {
  const recipe = getRecipe(recipeId);
  if (!recipe) return null;

  // Fonts first: a default that names a catalog (non-bundled) family is not in
  // document.fonts yet, and canvas text doesn't trigger loading by itself, so
  // the recipe would measure/rasterize the fallback face. Same collect+ensure
  // the sidebar's insert path runs. Soft-fail — a missing font must never fail
  // the thumb (fabric falls back; a fallback-metrics thumb is acceptable).
  try {
    const merged = params ? { ...recipe.defaults, ...params } : (recipe.defaults || {});
    const families = collectDraftFontFamilies(recipe.schema || [], merged);
    if (families.length > 0) {
      await Promise.allSettled(families.map((f) => ensureFont(f)));
    }
  } catch { /* soft — press on with whatever loaded */ }

  // Generate (defaults, or params merged over them). Async and allowed to
  // throw (a recipe needing data/assets, bad user params) → null render.
  let obj;
  try {
    obj = await generateSmartObject(recipeId, params || {});
  } catch {
    return null;
  }
  if (!obj) return null;

  const canvas = getCanvas(size);
  try {
    canvas.add(obj);
    obj.setCoords();

    // World bounding box (viewport-independent in fabric v6). Fit it into the
    // square with a small pad, then center via the canvas viewportTransform so
    // the object's own geometry is never touched.
    const pad = Math.round(size * 0.08);
    const avail = Math.max(1, size - pad * 2);
    const b = obj.getBoundingRect();
    const bw = Math.max(1, b.width);
    const bh = Math.max(1, b.height);
    let scale = Math.min(avail / bw, avail / bh);
    if (!Number.isFinite(scale) || scale <= 0) scale = 1;
    const cx = b.left + b.width / 2;
    const cy = b.top + b.height / 2;
    const tx = size / 2 - cx * scale;
    const ty = size / 2 - cy * scale;
    canvas.setViewportTransform([scale, 0, 0, scale, tx, ty]);

    canvas.renderAll();
    const blob = await toBlob(canvas.lowerCanvasEl);
    return URL.createObjectURL(blob);
  } catch {
    return null;
  } finally {
    try { canvas.remove(obj); } catch { /* not added */ }
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
  }
}

// ---------------------------------------------------------------- public API

/**
 * Render a smart-object recipe (at its defaults) → transparent PNG object URL.
 *
 * @param {object} opts
 *   recipeId — the ToolRecipe id (generateSmartObject runs it on defaults)
 *   size     — square thumb edge in px (default 96); the tile art downscales it
 * @returns {Promise<string|null>} blob: object URL (cache-owned; see
 *   releaseSmartObjectThumb), or null if the recipe is unknown / generate fails
 *   / the canvas can't encode — the caller shows a neutral placeholder.
 */
export function renderSmartObjectThumb({ recipeId, size = 96 } = {}) {
  if (!recipeId) return Promise.resolve(null);
  const version = getRecipe(recipeId)?.version;
  const key = keyFor(recipeId, version, size);
  const hit = cacheGet(key);
  if (hit) return Promise.resolve(hit); // sync fast path — no queue hop
  return enqueue(async () => {
    const raced = cacheGet(key); // an identical request ahead of us in the queue
    if (raced) return raced;
    const url = await renderOnce(recipeId, size);
    if (url) cachePut(key, url); // never cache a null — let a later call retry
    return url;
  });
}

/**
 * PARAM-DRIVEN preview render (SmartToolModal live pane): generate the recipe
 * at `params` merged over its defaults and return a transparent PNG object
 * URL. Rides the SAME serialized shared canvas as the thumbs, but is NEVER
 * cached — params vary per keystroke, an LRU would only churn. The CALLER
 * owns the returned URL and must URL.revokeObjectURL it when replaced.
 *
 * @param {object} opts
 *   recipeId — the ToolRecipe id
 *   params   — partial params merged over recipe defaults (draft values)
 *   size     — square render edge in px (default 512; the pane downscales)
 * @returns {Promise<string|null>} caller-owned blob URL, or null on any
 *   failure (unknown recipe / generate threw / encode failed).
 */
export function renderSmartObjectPreview({ recipeId, params = {}, size = 512 } = {}) {
  if (!recipeId) return Promise.resolve(null);
  return enqueue(() => renderOnce(recipeId, size, params));
}

/**
 * Release a thumb URL. URLs still owned by the cache are left alone (the cache
 * revokes on eviction/clear); anything already evicted is revoked now.
 */
export function releaseSmartObjectThumb(url) {
  if (!url) return;
  for (const owned of cache.values()) {
    if (owned === url) return;
  }
  try { URL.revokeObjectURL(url); } catch { /* already gone */ }
}

/** Drop every cached thumb and revoke all object URLs. */
export function clearSmartObjectThumbCache() {
  for (const url of cache.values()) {
    try { URL.revokeObjectURL(url); } catch { /* already gone */ }
  }
  cache.clear();
}
